import { LinkIcon } from "lucide-react";
import { useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";

import { JobSelectionDialog } from "./JobSelectionDialog";
import { OrphanedAtsJobTable } from "./OrphanedAtsJobTable";
import { Column, Row } from "@/components";
import { Button } from "@/components/ui/button";

import { useBatchUpdateOrphanedAtsJobs } from "@/hooks/mutations/useBatchUpdateOrphanedAtsJobs";
import { useGetOrphanedAtsJobs } from "@/hooks/queries/useGetOrphanedAtsJobs";
import { useSetAtsJobs } from "@/hooks/sets/useSetAtsJobs";
import { AtsJob } from "@/types";

export const OrphanedWorkableJobs = () => {
  const {
    data: orphanedAtsJobs,
    isError: isOrphanedAtsJobsError,
    isPending: isOrphanedAtsJobsPending,
  } = useGetOrphanedAtsJobs();
  const {
    mutate: batchUpdateOrphanedAtsJobs,
    isPending: isBatchUpdateOrphanedAtsJobsPending,
    isError: isBatchUpdateOrphanedAtsJobsError,
  } = useBatchUpdateOrphanedAtsJobs();
  const setAtsJobs = useSetAtsJobs();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [isJobSelectionDialogOpen, setIsJobSelectionDialogOpen] =
    useState(false);

  const handleSubmit = (jobId: string) => {
    batchUpdateOrphanedAtsJobs(
      {
        batchUpdateOrphanedAtsJobsRequest: {
          ids: Object.keys(rowSelection),
          laborupJobId: jobId,
        },
      },
      {
        onSuccess: (updatedAtsJobs: AtsJob[]) => {
          // Remove the ats jobs that were assigned to a laborup job from the list of orphaned jobs
          const atsJobIdsMoved = updatedAtsJobs.map((atsJob) => atsJob.id);
          const orphanedAtsJobsStillOrphaned = orphanedAtsJobs?.filter(
            (atsJob) => !atsJobIdsMoved.includes(atsJob.id),
          );

          setAtsJobs({
            atsJobs: orphanedAtsJobsStillOrphaned ?? [],
          });
          setRowSelection({});
        },
      },
    );
  };

  const render = () => {
    return (
      <Column className="gap-4 overflow-auto p-6">
        <Row className="justify-end">
          <Button
            className="rounded-full"
            disabled={Object.keys(rowSelection).length === 0}
            onClick={() => {
              setIsJobSelectionDialogOpen(true);
            }}
          >
            <LinkIcon className="h-4 w-4" />
            Link to Laborup Job
          </Button>
        </Row>
        <OrphanedAtsJobTable
          isOrphanedAtsJobsError={isOrphanedAtsJobsError}
          isOrphanedAtsJobsPending={isOrphanedAtsJobsPending}
          onRowSelectionChange={setRowSelection}
          orphanedJobs={orphanedAtsJobs ?? []}
          rowSelection={rowSelection}
        />
        <JobSelectionDialog
          handleSubmit={handleSubmit}
          isError={isBatchUpdateOrphanedAtsJobsError}
          isPending={isBatchUpdateOrphanedAtsJobsPending}
          onOpenChange={setIsJobSelectionDialogOpen}
          open={isJobSelectionDialogOpen}
        />
      </Column>
    );
  };

  return render();
};
